/**
 * Removes only auth/session-related keys from localStorage.
 *
 * Unlike localStorage.clear(), this preserves third-party SDK state
 * (LaunchDarkly, Segment, Highlight.io, etc.) so that pre-login
 * feature flag targeting and observability continue to work.
 *
 * ## Why not localStorage.clear()?
 *
 * The LOGOUT reducer, login form submit, and mobile logout all need to
 * clean up session state. Previously they used localStorage.clear(),
 * which wiped everything — including LaunchDarkly's anonymous device ID
 * (ld:$anonUserId). This made it impossible to target users with
 * feature flags before login, because Firebase's onIdTokenChanged fires
 * with null on the login page, dispatching LOGOUT and clearing LD state
 * that was just written during SDK init.
 *
 * ## Adding new keys
 *
 * When adding a new localStorage key to the app, decide whether it
 * should be cleared on logout:
 * - **User-scoped or auth data** (tokens, selected user/facility,
 *   per-user preferences): add it to SESSION_KEYS or SESSION_KEY_PREFIXES below.
 * - **Third-party SDK state** (ld:*, ajs_*, sessionID, highlightIdentifier):
 *   do NOT add it — these must survive logout.
 * - **Device/browser preferences** (facilityAppDownloadBannerVisible):
 *   do NOT add it — these are not user-scoped.
 */
export function clearSessionStorage(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && isSessionKey(key)) {
      localStorage.removeItem(key);
    }
  }
}

/** Exact localStorage keys to remove on logout. */
const SESSION_KEYS = new Set([
  "state", // Serialized Redux session state
  "AUTH_TOKEN", // Firebase auth token
  "selectedUser", // Currently selected user ID
  "selectedFacility", // Currently selected facility ID
  "emailToVerify", // Email used during sign-in link flow
  "emailForSignIn", // Email used during Firebase email sign-in
  "isSideMenuCollapsed", // Side menu UI state (user-scoped)
  "dateSelectionView", // Shift calendar view preference
  "disputeHoursInputPreference", // Invoice dispute input preference
]);

/** localStorage key prefixes to remove on logout (matches startsWith). */
const SESSION_KEY_PREFIXES = [
  "numberOfSession_", // Per-user session counter (numberOfSession_{userId})
  "CognitoIdentityServiceProvider", // AWS Cognito/Amplify auth tokens
  "claimWorkplaceFormData-", // Unverified placement claim form data per workplace
  "workerDocumentDownloadPollingRequest-", // Document download polling state per user
  "quizReviewStatus-", // Quiz completion status per user
];

function isSessionKey(key: string): boolean {
  return SESSION_KEYS.has(key) || SESSION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}
